import { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { supabase } from '../../lib/supabase';
import { FormSchema, FormField, FormTab, AssetFormSchema } from '../../types/field-builder';
import FieldEditor from './components/FieldEditor';
import TabEditor from './components/TabEditor';
import FormPreview from './components/FormPreview';
import VersionHistory from './components/VersionHistory';

export default function FieldBuilderPage() {
  const [schema, setSchema] = useState<FormSchema>({ tabs: [], fields: [] });
  const [currentSchemaId, setCurrentSchemaId] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<number>(1);
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'editor' | 'preview' | 'history'>('editor');
  const [selectedField, setSelectedField] = useState<FormField | null>(null);
  const [selectedTab, setSelectedTab] = useState<FormTab | null>(null);
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [showTabEditor, setShowTabEditor] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadActiveSchema();
  }, []);

  const loadActiveSchema = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/field-builder-operations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'get_active_schema' }),
        }
      );

      const result = await response.json();
      
      if (result.success && result.data) {
        setSchema(result.data.schema);
        setCurrentSchemaId(result.data.id);
        setCurrentVersion(result.data.version);
        setIsActive(result.data.is_active);
      }
    } catch (error) {
      console.error('Erro ao carregar schema:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchema = async () => {
    try {
      setSaving(true);
      const { data: { session } } = await supabase.auth.getSession();

      const action = currentSchemaId ? 'update_version' : 'create_version';
      const payload = currentSchemaId 
        ? { action, data: { id: currentSchemaId, schema } }
        : { action, data: { schema } };

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/field-builder-operations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert('✅ Configuração salva com sucesso!');
        if (!currentSchemaId) {
          setCurrentSchemaId(result.data.id);
          setCurrentVersion(result.data.version);
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('❌ Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishSchema = async () => {
    if (!currentSchemaId) {
      alert('Salve a configuração antes de publicar');
      return;
    }

    if (!confirm('Deseja publicar esta versão? Ela se tornará a versão ativa do formulário.')) {
      return;
    }

    try {
      setSaving(true);
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/field-builder-operations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'publish_version',
            data: { id: currentSchemaId }
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setIsActive(true);
        alert('✅ Versão publicada com sucesso!');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Erro ao publicar:', error);
      alert('❌ Erro ao publicar versão');
    } finally {
      setSaving(false);
    }
  };

  const handleAddField = () => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      key: `campo_${Date.now()}`,
      label: 'Novo Campo',
      type: 'text',
      required: false,
      tab_id: schema.tabs[0]?.id || '',
      order: schema.fields.length + 1,
      placeholder: '',
      help_text: '',
    };
    setSelectedField(newField);
    setShowFieldEditor(true);
  };

  const handleEditField = (field: FormField) => {
    setSelectedField(field);
    setShowFieldEditor(true);
  };

  const handleSaveField = (field: FormField) => {
    const existingIndex = schema.fields.findIndex(f => f.id === field.id);
    
    if (existingIndex >= 0) {
      const updatedFields = [...schema.fields];
      updatedFields[existingIndex] = field;
      setSchema({ ...schema, fields: updatedFields });
    } else {
      setSchema({ ...schema, fields: [...schema.fields, field] });
    }
    
    setShowFieldEditor(false);
    setSelectedField(null);
  };

  const handleDeleteField = (fieldId: string) => {
    if (!confirm('Deseja realmente excluir este campo?')) return;
    
    setSchema({
      ...schema,
      fields: schema.fields.filter(f => f.id !== fieldId)
    });
  };

  const handleAddTab = () => {
    const newTab: FormTab = {
      id: `tab_${Date.now()}`,
      label: 'Nova Aba',
      order: schema.tabs.length + 1,
    };
    setSelectedTab(newTab);
    setShowTabEditor(true);
  };

  const handleEditTab = (tab: FormTab) => {
    setSelectedTab(tab);
    setShowTabEditor(true);
  };

  const handleSaveTab = (tab: FormTab) => {
    const existingIndex = schema.tabs.findIndex(t => t.id === tab.id);
    
    if (existingIndex >= 0) {
      const updatedTabs = [...schema.tabs];
      updatedTabs[existingIndex] = tab;
      setSchema({ ...schema, tabs: updatedTabs });
    } else {
      setSchema({ ...schema, tabs: [...schema.tabs, tab] });
    }
    
    setShowTabEditor(false);
    setSelectedTab(null);
  };

  const handleDeleteTab = (tabId: string) => {
    const hasFields = schema.fields.some(f => f.tab_id === tabId);
    
    if (hasFields) {
      alert('Não é possível excluir uma aba que contém campos. Mova ou exclua os campos primeiro.');
      return;
    }
    
    if (!confirm('Deseja realmente excluir esta aba?')) return;
    
    setSchema({
      ...schema,
      tabs: schema.tabs.filter(t => t.id !== tabId)
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = schema.fields.findIndex(f => f.id === active.id);
      const newIndex = schema.fields.findIndex(f => f.id === over.id);

      const reorderedFields = arrayMove(schema.fields, oldIndex, newIndex).map((field, index) => ({
        ...field,
        order: index + 1
      }));

      setSchema({ ...schema, fields: reorderedFields });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Field Builder</h1>
              <p className="text-sm text-gray-600 mt-1">
                Configuração do Formulário de Cadastro de Ativos
                {isActive && <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Versão Ativa</span>}
                <span className="ml-2 text-gray-400">v{currentVersion}</span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveSchema}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Salvar Rascunho
                  </>
                )}
              </button>
              <button
                onClick={handlePublishSchema}
                disabled={saving || !currentSchemaId}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                <i className="ri-checkbox-circle-line"></i>
                Publicar Versão
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('editor')}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'editor'
                  ? 'text-teal-600 border-b-2 border-teal-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-edit-line mr-2"></i>
              Editor
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'preview'
                  ? 'text-teal-600 border-b-2 border-teal-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-eye-line mr-2"></i>
              Pré-visualização
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'history'
                  ? 'text-teal-600 border-b-2 border-teal-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-history-line mr-2"></i>
              Histórico de Versões
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'editor' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Abas */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Abas</h2>
                <button
                  onClick={handleAddTab}
                  className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  Nova Aba
                </button>
              </div>
              <div className="space-y-2">
                {schema.tabs.sort((a, b) => a.order - b.order).map((tab) => (
                  <div
                    key={tab.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <i className="ri-drag-move-line text-gray-400"></i>
                      <span className="font-medium text-gray-900">{tab.label}</span>
                      <span className="text-xs text-gray-500">
                        ({schema.fields.filter(f => f.tab_id === tab.id).length} campos)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditTab(tab)}
                        className="p-1.5 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded cursor-pointer"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={() => handleDeleteTab(tab.id)}
                        className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Campos */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Campos</h2>
                <button
                  onClick={handleAddField}
                  className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  Novo Campo
                </button>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <div className="space-y-4">
                  {schema.tabs.sort((a, b) => a.order - b.order).map((tab) => {
                    const tabFields = schema.fields
                      .filter(f => f.tab_id === tab.id)
                      .sort((a, b) => a.order - b.order);

                    if (tabFields.length === 0) return null;

                    return (
                      <div key={tab.id}>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <i className="ri-folder-line"></i>
                          {tab.label}
                        </h3>
                        <SortableContext
                          items={tabFields.map(f => f.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {tabFields.map((field) => (
                              <div
                                key={field.id}
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <i className="ri-drag-move-line text-gray-400 cursor-move"></i>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-gray-900">{field.label}</span>
                                      {field.required && (
                                        <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                                          Obrigatório
                                        </span>
                                      )}
                                      {field.readonly && (
                                        <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">
                                          Somente leitura
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                      <span>Chave: {field.key}</span>
                                      <span>•</span>
                                      <span>Tipo: {field.type}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleEditField(field)}
                                    className="p-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded cursor-pointer"
                                  >
                                    <i className="ri-edit-line"></i>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteField(field.id)}
                                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded cursor-pointer"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </SortableContext>
                      </div>
                    );
                  })}
                </div>
              </DndContext>
            </div>
          </div>
        )}

        {activeTab === 'preview' && (
          <FormPreview schema={schema} />
        )}

        {activeTab === 'history' && (
          <VersionHistory onLoadVersion={loadActiveSchema} />
        )}
      </div>

      {/* Modais */}
      {showFieldEditor && selectedField && (
        <FieldEditor
          field={selectedField}
          tabs={schema.tabs}
          onSave={handleSaveField}
          onClose={() => {
            setShowFieldEditor(false);
            setSelectedField(null);
          }}
        />
      )}

      {showTabEditor && selectedTab && (
        <TabEditor
          tab={selectedTab}
          onSave={handleSaveTab}
          onClose={() => {
            setShowTabEditor(false);
            setSelectedTab(null);
          }}
        />
      )}
    </div>
  );
}
